import type {
  ApiEnvelope,
  AuthUser,
  ForgotPasswordPayload,
  LoginPayload,
  ResetPasswordPayload
} from '~/types/types'

interface BackendMembership {
  tenant_id?: number
  tenant_slug?: string
  tenant_name?: string
  tenant_logo?: string | null
  tenant_cover_image?: string | null
  tenant_plan?: string
  url?: string | null
  role?: 'admin' | 'moderator' | 'member'
  status?: 'active' | 'pending' | 'suspended'
  expires_at?: string | null
  is_free?: boolean
  membership_price?: number | null
  currency?: string | null
}

interface BackendOwnedTenant {
  id?: number
  name?: string
  slug?: string
  plan?: string
  provisioning_status?: 'pending' | 'provisioning' | 'ready' | 'failed'
  subscription_status?: string
  trial_ends_at?: string | null
  business_type?: string
  logo?: string | null
  cover_image?: string | null
  url?: string | null
  is_free?: boolean
  membership_price?: number | null
  currency?: string | null
}

interface BackendUser {
  id: number
  name: string
  username?: string
  email: string
  avatar?: string | null
  bio?: string | null
  country?: string | null
  mobile?: string | null
  locale?: string | null
  timezone?: string | null
  referral_code?: string | null
  role?: 'admin' | 'member'
  email_verified_at?: string | null
  memberships?: BackendMembership[]
  owned_tenants?: BackendOwnedTenant[]
}

export interface RegisterPayload {
  name: string
  email: string
  password: string
  password_confirmation: string
  agency_name: string
  slug?: string
  business_type: string
  main_currency?: string
  timezone?: string
  locale?: string
}

export interface RegisteredTenant {
  id: number
  slug: string
  name: string
  provisioning_status: 'pending' | 'provisioning' | 'ready' | 'failed'
  url: string | null
}

type UnknownRecord = Record<string, unknown>
type AuthResponsePayload = ApiEnvelope<BackendUser> | BackendUser | UnknownRecord

export const useAuthRepository = () => {
  const api = useApi()
  const authToken = useAuthToken()

  const asRecord = (value: unknown): UnknownRecord | null =>
    value && typeof value === 'object' ? (value as UnknownRecord) : null

  const toAuthUser = (value: BackendUser): AuthUser => ({
    id: value.id,
    name: value.name,
    username: value.username ?? '',
    email: value.email,
    avatar: value.avatar ?? null,
    bio: value.bio ?? null,
    country: value.country ?? null,
    mobile: value.mobile ?? null,
    locale: value.locale ?? null,
    timezone: value.timezone ?? null,
    referral_code: value.referral_code ?? null,
    role: value.role,
    email_verified_at: value.email_verified_at ?? null,
    memberships: Array.isArray(value.memberships)
      ? value.memberships.map(item => ({
          tenant_id: Number(item.tenant_id || 0),
          tenant_slug: String(item.tenant_slug || ''),
          tenant_name: item.tenant_name || undefined,
          tenant_logo: item.tenant_logo || undefined,
          tenant_cover_image: item.tenant_cover_image || undefined,
          tenant_plan: item.tenant_plan || undefined,
          url: item.url || null,
          role: item.role || 'member',
          status: item.status || 'active',
          expires_at: item.expires_at ?? null,
          is_free: item.is_free,
          membership_price: item.membership_price ?? null,
          currency: item.currency ?? null
        })).filter(item => item.tenant_id > 0 && item.tenant_slug.length > 0)
      : [],
    owned_tenants: Array.isArray(value.owned_tenants)
      ? value.owned_tenants
          .filter(t => t.id && t.slug)
          .map(t => ({
            id: Number(t.id),
            name: String(t.name || t.slug),
            slug: String(t.slug),
            plan: (t.plan as string) || 'starter',
            provisioning_status: t.provisioning_status,
            subscription_status: t.subscription_status,
            trial_ends_at: t.trial_ends_at ?? null,
            business_type: t.business_type,
            logo: t.logo ?? null,
            cover_image: t.cover_image ?? null,
            url: t.url ?? null,
            is_free: t.is_free,
            membership_price: t.membership_price ?? null,
            currency: t.currency ?? null
          }))
      : undefined
  })

  const extractToken = (value: unknown): string | null => {
    const record = asRecord(value)
    if (!record) return null

    const directToken = record.token
    if (typeof directToken === 'string' && directToken.trim()) {
      return directToken.trim()
    }

    const accessToken = record.access_token
    if (typeof accessToken === 'string' && accessToken.trim()) {
      return accessToken.trim()
    }

    return extractToken(record.data)
  }

  const extractUser = (value: unknown): AuthUser | null => {
    const record = asRecord(value)
    if (!record) return null

    const maybeUser = asRecord(record.user)
    if (maybeUser?.id && typeof maybeUser.name === 'string' && typeof maybeUser.email === 'string') {
      return toAuthUser(maybeUser as unknown as BackendUser)
    }

    if (record.id && typeof record.name === 'string' && typeof record.email === 'string') {
      return toAuthUser(record as unknown as BackendUser)
    }

    return extractUser(record.data)
  }

  const login = async (payload: LoginPayload): Promise<{ user: AuthUser | null; requiresEmailVerification: boolean }> => {
    const { remember, ...credentials } = payload
    const shouldRemember = Boolean(remember)

    const response = await api.post<AuthResponsePayload>('/api/auth/login', { body: credentials })
    const token = extractToken(response)
    authToken.setToken(token, shouldRemember)

    if (!token) {
      throw createError({ statusCode: 500, statusMessage: 'Authentication token was not returned by backend.' })
    }

    const record = asRecord(response)
    const requiresEmailVerification = Boolean(record?.requires_email_verification)

    return { user: extractUser(response), requiresEmailVerification }
  }

  /**
   * Self-serve agency signup. Same token/OTP contract as login: the backend
   * returns a bearer token and requires_email_verification=true.
   */
  const register = async (payload: RegisterPayload): Promise<{ user: AuthUser | null, requiresEmailVerification: boolean, tenant: RegisteredTenant | null }> => {
    const response = await api.post<AuthResponsePayload>('/api/auth/register', { body: payload })
    const token = extractToken(response)
    authToken.setToken(token, true)

    if (!token) {
      throw createError({ statusCode: 500, statusMessage: 'Authentication token was not returned by backend.' })
    }

    const record = asRecord(response)
    const tenant = (record?.tenant as RegisteredTenant | undefined) ?? null
    return {
      user: extractUser(response),
      requiresEmailVerification: Boolean(record?.requires_email_verification),
      tenant
    }
  }

  const me = async (): Promise<AuthUser | null> => {
    if (!authToken.token.value) {
      return null
    }

    try {
      const response = await api.get<ApiEnvelope<BackendUser> | BackendUser>('/api/auth/me')
      return extractUser(response)
    } catch (error) {
      const statusCode = (error as { statusCode?: number })?.statusCode
      if (statusCode === 401) {
        authToken.clearToken()
        return null
      }
      throw error
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout')
    } finally {
      authToken.clearToken()
    }
  }

  const forgotPassword = async (payload: ForgotPasswordPayload): Promise<void> => {
    await api.post('/api/auth/forgot-password', { body: payload })
  }

  const resetPassword = async (payload: ResetPasswordPayload): Promise<void> => {
    await api.post('/api/auth/reset-password', { body: payload })
  }

  const resendEmailVerification = async (): Promise<void> => {
    await api.post('/api/auth/resend-verification')
  }

  const getGoogleRedirectUrl = async (referralCode?: string | null): Promise<string> => {
    const params = new URLSearchParams()
    if (referralCode) params.set('referral_code', referralCode)
    if (import.meta.client) params.set('origin', window.location.origin)
    const response = await api.get<{ url: string }>(`/api/auth/google/redirect?${params}`)
    const url = (response as { url: string }).url

    // SECURITY: validate that the URL points at Google's OAuth endpoint before
    // we navigate to it. Defends against a compromised backend redirecting users
    // to a phishing page.
    try {
      const parsed = new URL(url)
      const allowed = ['accounts.google.com', 'oauth2.googleapis.com']
      if (parsed.protocol !== 'https:' || !allowed.includes(parsed.hostname)) {
        throw createError({ statusCode: 502, statusMessage: 'Unexpected OAuth redirect target.' })
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'statusCode' in err) throw err
      throw createError({ statusCode: 502, statusMessage: 'Invalid OAuth redirect URL.' })
    }

    return url
  }

  const handleGoogleCallback = async (token: string): Promise<AuthUser | null> => {
    authToken.setToken(token)
    const response = await api.get<ApiEnvelope<BackendUser> | BackendUser>('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
    return extractUser(response)
  }

  const verifyEmailOtp = async (otp: string): Promise<AuthUser | null> => {
    const response = await api.post<AuthResponsePayload>('/api/auth/email/otp/verify', { body: { otp } })
    return extractUser(response)
  }

  const resendEmailOtp = async (): Promise<void> => {
    await api.post('/api/auth/email/otp/resend')
  }

  return {
    login,
    register,
    me,
    logout,
    forgotPassword,
    resetPassword,
    resendEmailVerification,
    getGoogleRedirectUrl,
    handleGoogleCallback,
    verifyEmailOtp,
    resendEmailOtp
  }
}
